"use client";

import { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { calculateAmount } from "app/payment/src/card-details/MainPaymentType/calculateAmount";
import useCurrency from "hooks/swr/useCurrency";
import { useIsAgent } from "hooks/useIsAgent";
import { selectBookingStore, selectPdpFees, selectTravelProtectionCost } from "stores/booking/bookingSlice";
import { useAppSelector } from "stores/hooks";
import { useBookingSummary } from "./useBookingSummary";
type Props = {
  size?: "card" | "sticky";
  dateGuestSelector: ReactNode;
  isTravelAgentClient?: boolean;
  trailingAction?: ReactNode;
};
const BookingPriceHeader = ({
  size = "card",
  dateGuestSelector,
  isTravelAgentClient = false,
  trailingAction
}: Props) => {
  const {
    formatter,
    rate
  } = useCurrency();
  const {
    daysLength,
    totalAmt,
    taCommission
  } = useBookingSummary();
  const bookingStore = useAppSelector(selectBookingStore);
  const pdpFees = useAppSelector(selectPdpFees);
  const travelProtectionCost = useAppSelector(selectTravelProtectionCost);
  const isCard = size === "card";
  const priceSize = isCard ? "text-[22px]" : "text-[16px]";
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  const isAgent = useIsAgent();

  // Calculate the amount to be paid
  const amount = calculateAmount(bookingStore, travelProtectionCost, pdpFees, rate);
  return <div className='flex w-full flex-col gap-2' data-sentry-component="BookingPriceHeader" data-sentry-source-file="BookingPriceHeader.tsx">
      <div className='flex items-baseline justify-between gap-3'>
        <p className='shrink-0 text-[13px] font-medium leading-[1.2] tracking-[-0.13px] text-Gray-800'>
          {daysLength} {daysLength === 1 ? "night" : "nights"}
        </p>
        {dateGuestSelector}
      </div>

      {!isAgent ? <div className='flex items-end gap-1 whitespace-nowrap'>
          <p className='text-[0px] font-bold leading-[0] text-textColor'>
            <span className={`${priceSize} leading-none`}>
              {isPaymentPage ? formatter(2, 2).format(amount) : formatter(0, 0).format(totalAmt * rate)}
            </span>
            <span className='text-[14px] leading-none'> total</span>
          </p>
          {<p className='mb-[2px] text-[12px] font-medium leading-none tracking-[-0.24px] text-Gray-700'>
              Includes taxes and fees
            </p>}
        </div> : <div className='flex items-center gap-2'>
          <div className='min-w-px flex flex-1 items-end gap-1 whitespace-nowrap'>
            <p className='text-[0px] font-bold leading-[0] text-textColor'>
              <span className={`${priceSize} leading-none`}>
                {isPaymentPage ? formatter(2, 2).format(amount) : formatter(0, 0).format(totalAmt * rate)}
              </span>
              <span className='text-[14px] leading-none'> total</span>
            </p>
            {taCommission > 0 && <p className='mb-[2px] text-[12px] font-medium leading-none tracking-[-0.24px] text-Gray-700'>
                Includes {formatter(0, 0).format(taCommission * rate)}{" "}
                commission
              </p>}
          </div>
          {trailingAction}
        </div>}
    </div>;
};
export default BookingPriceHeader;